/**
 * Slice 22 — Hair material patch (May 16 2026; disabled May 17 2026).
 *
 * F4 hair materials in our UE5 → FBX → Blender → GLB pipeline ship with
 * `alphaMode: BLEND`. Three.js maps that to `transparent = true`,
 * `alphaTest = 0`, `depthWrite = false`.
 *
 * History of failed patches (every variant looked WORSE than the raw
 * GLB on at least one model):
 *   - May 16 v1: `alphaTest = 0.33` + `transparent = false`. Naomi's
 *     wispy Yuki bun has hair-card alpha rarely > 0.33; most of the
 *     bun was culled → she rendered nearly bald (P0 regression).
 *   - May 17 v2: `alphaTest = 0` + keep BLEND. Ashley's scalp showed
 *     through the wispy hair cards.
 *   - May 17 v3: `alphaTest = 0.05` + `alphaToCoverage`. Looked
 *     painterly / brushstroked on Kiera, Mia, Ashley. premultipliedAlpha
 *     made Kiera invisible (GLB textures use straight alpha).
 *   - May 17 v4: `depthWrite = true` only. Worse than disabled — the
 *     low-alpha hair-card pixels still wrote to depth, culling
 *     everything behind them, exposing the scalp again.
 *
 * Conclusion: the artist-authored material settings are correct. The
 * helper stays no-op. If hair on a specific model needs improvement,
 * fix it upstream in the asset pipeline (Blender / UE5 source) and
 * re-export — not at runtime.
 *
 * Conservative gating:
 *   - Only patches materials that currently look like a Three.js BLEND
 *     mapping (`material.transparent === true && material.alphaTest === 0`).
 *   - Only patches when the mesh / material name suggests hair (and
 *     explicitly NOT eyelashes / brows / fluid / occlusion — those
 *     legitimately use BLEND for translucent fur / rim shading).
 *
 * The current implementation is a no-op. See the `History of failed
 * patches` block above for the reason. The `isPatchableHairMesh`
 * predicate and the helper are retained so call sites in Avatar.tsx +
 * wardrobeLoader.ts continue to compile, and so a future, narrower
 * fix can be slotted in.
 */

import * as THREE from 'three';

/**
 * Predicate that returns true if `node` looks like a hair mesh and its
 * material currently looks like a Three.js mapping of a GLTF BLEND
 * material (which is what we want to flip).
 *
 * Exported for testing / debugging — the patch function below uses
 * the same logic.
 */
export function isPatchableHairMesh(node: THREE.Object3D): boolean {
  const asMesh = node as unknown as THREE.Mesh;
  if (!asMesh.isMesh && !(asMesh as unknown as THREE.SkinnedMesh).isSkinnedMesh) {
    return false;
  }
  const mat = asMesh.material as THREE.Material | undefined;
  if (!mat) return false;
  if (Array.isArray(mat)) return false;

  // BLEND-mapping detection: a Three.js GLTFLoader-loaded material
  // that came from a GLTF BLEND alphaMode has these properties set.
  const matAny = mat as unknown as { transparent: boolean; alphaTest?: number };
  const looksLikeBlend = matAny.transparent === true && (matAny.alphaTest ?? 0) === 0;
  if (!looksLikeBlend) return false;

  const meshName = node.name.toLowerCase();
  const matName = String((mat as { name?: string }).name || '').toLowerCase();

  // Positive matches: anything name-tagged hair, plus the hair styles
  // that don't include the word "hair" (Cara's Yuki bangs).
  const looksLikeHair =
    meshName.includes('hair') ||
    matName.includes('hair') ||
    meshName.includes('bangs') ||
    matName.includes('bangs') ||
    meshName.includes('yuki') ||
    matName.includes('yuki');
  if (!looksLikeHair) return false;

  // Negative matches: legitimately-translucent layers we MUST NOT touch.
  const isExcluded =
    meshName.includes('eyelash') ||
    matName.includes('eyelash') ||
    meshName.includes('lash') ||
    matName.includes('lash') ||
    meshName.includes('brow') ||
    matName.includes('brow') ||
    meshName.includes('fluid') ||
    matName.includes('fluid') ||
    meshName.includes('occlusion') ||
    matName.includes('occlusion');
  if (isExcluded) return false;

  return true;
}

/**
 * Patch a single hair material in-place. No-op if `node` is not a
 * patchable hair mesh.
 *
 * Returns the new material (mutated in place) or null if no patch was
 * applied.
 */
export function patchHairMaterial(node: THREE.Object3D): THREE.Material | null {
  // No-op (May 17 2026). Every variant attempted on this code path
  // (alphaTest=0.33 / 0 / 0.05, alphaToCoverage, RGB / opacity clamps,
  // depthWrite=true) looked worse than the raw GLB on at least one
  // model. The artist-authored materials are correct; Three.js's
  // default BLEND mapping is what we want. Helper kept in the tree so
  // the call site in Avatar.tsx + wardrobeLoader.ts continue to compile.
  // Re-enable only if a regression is reproducible on a specific model
  // — and then prefer a per-model override (in the GLB asset pipeline
  // upstream of this code path), not a global runtime patch.
  if (!isPatchableHairMesh(node)) return null;
  return null;
}

/**
 * Walk a scene subtree and patch every hair material under it. Used
 * at GLB-load time (Avatar.tsx) and after wardrobe-piece mount
 * (wardrobeLoader.ts).
 *
 * Returns the number of materials patched.
 */
export function patchHairMaterialsInTree(root: THREE.Object3D): number {
  let count = 0;
  root.traverse((node) => {
    if (patchHairMaterial(node)) count++;
  });
  return count;
}
